/**
 * Keeps the Sarah launcher off page controls (Muse audit 2026-10-20, C1). The
 * fixed bottom-end disc covered the About closings carousel Next button, a
 * home radio, and a listing map zoom control.
 *
 * After every scroll or resize settles, the launcher measures the focusable
 * controls under it. If one is blocked, it lifts just above it; if no lift up
 * to the cap clears every control, it tucks away until the next scroll. The
 * e2e spec (e2e/sarah-launcher-overlap.spec.ts) holds the same rule in a real
 * browser with the same predicate, so the two cannot drift.
 *
 * Geometry is pure. Only collectFocusableRects touches the DOM.
 */

export type Box = {
  left: number
  top: number
  right: number
  bottom: number
  /**
   * C1 (2026-10-21): links, buttons, and form fields block on any overlap, card
   * links included, so the disc never sits on something a visitor taps. Other
   * focusable surfaces (map canvas, iframe, tabindex regions) keep the large
   * surface share rule.
   */
  strict?: boolean
}

/** Controls that block on any overlap, whatever their size. */
export const SARAH_STRICT_SELECTOR = 'a[href],button,input,select,textarea'

/** Anything a visitor can tab to or click. */
export const SARAH_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="link"]',
  '[role="radio"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="slider"]',
  '[role="tab"]',
].join(',')

/**
 * A control the size of a button, radio, chip, input, or zoom control. A
 * surface bigger than this (a whole card link, a map canvas, a gallery) is
 * still usable when a corner is covered, so it blocks only when the launcher
 * covers a real share of it.
 */
export const SARAH_COMPACT_MAX_W = 320
export const SARAH_COMPACT_MAX_H = 160
export const SARAH_LARGE_COVER_SHARE = 0.25

/** The edge tab's box (CSS .v3-sarah__restore: 3rem by 2.75rem, flush right), px. */
export const SARAH_EDGE_TAB_W = 48
export const SARAH_EDGE_TAB_H = 44
/** Width of the right-edge band measured for the disc, its X and the tab, px. */
export const SARAH_PAINT_BAND_RIGHT = 160

/** Quiet time after the last scroll before the launcher re-measures, ms. */
export const SARAH_SCROLL_SETTLE_MS = 160
/** Room left between the lifted launcher and the control under it, px. */
export const SARAH_LIFT_GAP = 8
/** Never lift more than this share of the viewport height. */
export const SARAH_MAX_LIFT_SHARE = 0.45
/** Never lift into the top band where the site header lives, px. */
export const SARAH_TOP_RESERVE = 88

export function boxWidth(b: Box): number {
  return Math.max(0, b.right - b.left)
}

export function boxHeight(b: Box): number {
  return Math.max(0, b.bottom - b.top)
}

export function intersectionArea(a: Box, b: Box): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left)
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top)
  return w > 0 && h > 0 ? w * h : 0
}

export function boxesIntersect(a: Box, b: Box): boolean {
  return intersectionArea(a, b) > 0
}

/** Slider thumbs paint past the input's own box; pad range inputs by this, px. */
export const SARAH_RANGE_PAD = 16

export function padBox(b: Box, pad: number): Box {
  return { ...b, left: b.left - pad, top: b.top - pad, right: b.right + pad, bottom: b.bottom + pad }
}

export function shiftBox(b: Box, dy: number): Box {
  return { left: b.left, right: b.right, top: b.top - dy, bottom: b.bottom - dy }
}

/** True when the launcher box makes the control hard to use. */
export function launcherBlocks(launcher: Box, control: Box): boolean {
  const area = intersectionArea(launcher, control)
  if (area <= 0) return false
  if (control.strict) return true
  const w = boxWidth(control)
  const h = boxHeight(control)
  if (w <= SARAH_COMPACT_MAX_W && h <= SARAH_COMPACT_MAX_H) return true
  return area / Math.max(1, w * h) >= SARAH_LARGE_COVER_SHARE
}

export function blockedControls(launcher: Box, controls: readonly Box[]): Box[] {
  return controls.filter((c) => launcherBlocks(launcher, c))
}

export type LauncherPlacement = { mode: 'rest' } | { mode: 'lifted'; lift: number } | { mode: 'tucked' }

/**
 * Where the launcher sits. `rest` is its box with no lift. Candidate lifts put
 * its bottom just above each blocked control, lowest first. The first that
 * clears every control wins; none within the cap tucks it away.
 */
export function placeLauncher(input: {
  rest: Box
  controls: readonly Box[]
  viewportHeight: number
  /** Other boxes that move with the disc (its dismiss X), at rest. */
  extras?: readonly Box[]
}): LauncherPlacement {
  const { rest, controls, viewportHeight } = input
  const extras = input.extras ?? []
  const clears = (lift: number) =>
    [rest, ...extras].every((b) => blockedControls(shiftBox(b, lift), controls).length === 0)
  if (clears(0)) return { mode: 'rest' }
  const maxLift = Math.max(0, Math.min(viewportHeight * SARAH_MAX_LIFT_SHARE, rest.top - SARAH_TOP_RESERVE))
  const colLeft = Math.min(rest.left, ...extras.map((b) => b.left))
  const candidates = [
    ...new Set(
      controls
        .filter((c) => c.top < rest.bottom && c.right > colLeft && c.left < rest.right)
        .map((c) => Math.ceil(rest.bottom - c.top + SARAH_LIFT_GAP))
        .filter((lift) => lift > 0 && lift <= maxLift),
    ),
  ].sort((a, b) => a - b)
  for (const lift of candidates) {
    if (clears(lift)) return { mode: 'lifted', lift }
  }
  return { mode: 'tucked' }
}

export type EdgeTabSlot = { side: 'right' | 'left'; top: number; slim: boolean }

/** The slim tab: a 34px visible pill inside a 44px hit area. */
export const SARAH_EDGE_TAB_SLIM_VISIBLE = 34
export const SARAH_EDGE_TAB_SLIM_HIT = 44
/** Step used when the tab slides along an edge, px. */
export const SARAH_EDGE_TAB_STEP = 4

/**
 * Where the collapsed edge tab goes when the disc has no clear spot. It slides
 * along the right edge over the full viewport height, nearest its resting
 * bottom first, then tries the left edge; then the same with the slim tab.
 * Its whole hit area must block no control. Null means keep it hidden.
 */
export function placeEdgeTab(input: {
  controls: readonly Box[]
  viewportWidth: number
  viewportHeight: number
  /** The tab's preferred bottom (the disc's resting bottom), px. */
  restBottom: number
}): EdgeTabSlot | null {
  const { controls, viewportWidth: vw, viewportHeight: vh, restBottom } = input
  const sizes = [
    { w: SARAH_EDGE_TAB_W, h: SARAH_EDGE_TAB_H, slim: false },
    { w: SARAH_EDGE_TAB_SLIM_HIT, h: SARAH_EDGE_TAB_SLIM_HIT, slim: true },
  ]
  for (const size of sizes) {
    const maxTop = Math.max(0, vh - size.h)
    const pref = Math.min(maxTop, Math.max(0, restBottom - size.h))
    const tops: number[] = [pref]
    for (let d = SARAH_EDGE_TAB_STEP; d <= vh; d += SARAH_EDGE_TAB_STEP) {
      if (pref - d >= 0) tops.push(pref - d)
      if (pref + d <= maxTop) tops.push(pref + d)
    }
    for (const side of ['right', 'left'] as const) {
      const left = side === 'right' ? vw - size.w : 0
      for (const top of tops) {
        const box = { left, right: left + size.w, top, bottom: top + size.h }
        if (blockedControls(box, controls).length === 0) return { side, top, slim: size.slim }
      }
    }
  }
  return null
}

function visibleBox(el: Element, viewport: { width: number; height: number }): Box | null {
  const r = el.getBoundingClientRect()
  if (r.width <= 0 || r.height <= 0) return null
  if (r.bottom <= 0 || r.top >= viewport.height || r.right <= 0 || r.left >= viewport.width) return null
  const style = typeof getComputedStyle === 'function' ? getComputedStyle(el) : null
  if (style && (style.visibility === 'hidden' || style.pointerEvents === 'none')) return null
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom }
}

/**
 * C1 (2026-10-21): a control only blocks the launcher if a visitor can see
 * and reach it. Skips controls that are fully transparent (opacity 0 on it or
 * an ancestor), fully clipped by an overflow ancestor, or covered by other
 * content at their visible center (elementFromPoint). Returns the box clipped
 * to its overflow ancestors, or null. The covers check script
 * (look-pass covers-*.cjs) uses this same definition.
 */
export function reachableBox(
  el: Element,
  box: Box,
  doc: Document,
  exclude: Element | null,
  viewport: { width: number; height: number },
): Box | null {
  const view = doc.defaultView
  const cs = (n: Element) => (view && typeof view.getComputedStyle === 'function' ? view.getComputedStyle(n) : null)
  let b: Box = { ...box }
  for (let n: Element | null = el; n && n !== doc.documentElement; n = n.parentElement) {
    const st = cs(n)
    if (!st) break
    if (st.opacity !== '' && Number(st.opacity) === 0) return null
    if (n !== el && /(hidden|clip|scroll|auto)/.test(`${st.overflow}${st.overflowX}${st.overflowY}`)) {
      const c = n.getBoundingClientRect()
      if (c.width > 0 || c.height > 0) {
        b = {
          left: Math.max(b.left, c.left),
          top: Math.max(b.top, c.top),
          right: Math.min(b.right, c.right),
          bottom: Math.min(b.bottom, c.bottom),
        }
        if (b.right <= b.left || b.bottom <= b.top) return null
      }
    }
  }
  if (typeof doc.elementFromPoint === 'function') {
    const cx = (Math.max(0, b.left) + Math.min(viewport.width, b.right)) / 2
    const cy = (Math.max(0, b.top) + Math.min(viewport.height, b.bottom)) / 2
    const hit = doc.elementFromPoint(cx, cy)
    if (hit && hit !== el && !el.contains(hit) && !(exclude && exclude.contains(hit))) return null
  }
  return b
}

/**
 * Viewport boxes of every visible focusable control, minus Sarah's own UI and
 * anything inside a closed (hidden or inert) subtree.
 */
export function collectFocusableRects(doc: Document, exclude: Element | null): Box[] {
  const view = doc.defaultView
  const viewport = { width: view?.innerWidth ?? 0, height: view?.innerHeight ?? 0 }
  const out: Box[] = []
  for (const el of doc.querySelectorAll(SARAH_FOCUSABLE_SELECTOR)) {
    if (exclude && exclude.contains(el)) continue
    if (el.closest('[hidden],[inert]')) continue
    const seen = visibleBox(el, viewport)
    const box = seen ? reachableBox(el, seen, doc, exclude, viewport) : null
    if (!box) continue
    const isRange = typeof el.matches === 'function' && el.matches('input[type="range"],[role="slider"]')
    const sized = isRange ? padBox(box, SARAH_RANGE_PAD) : box
    out.push(typeof el.matches === 'function' && el.matches(SARAH_STRICT_SELECTOR) ? { ...sized, strict: true } : sized)
  }
  return out
}
